/**
 * Signals: a wave, and four things people actually say.
 *
 * The wave is the interesting one. At twelve metres in a crowd the geometry has run out and the
 * product already says so — "look around and wave". The trouble is that both people are looking
 * around, and neither knows when the other is looking. A wave signal buzzes the other phones
 * hard and strobes their screens for a second: *now*. That is the human handoff made mutual,
 * and it is worth more than another decimal place of accuracy.
 *
 * The other four exist to stop people leaving. "On my way", "I'm here", "5 minutes", "Come to me
 * instead" — the sentences that otherwise send somebody to WhatsApp mid-session, which is where
 * sessions go to die. No keyboard, no history, nothing stored anywhere.
 */

import { SIGNALS, signalLabel, type SignalKind } from '@beeline/shared';

import { el } from '../lib/dom';

export interface SignalsOptions {
  onSend: (signal: SignalKind) => void;
}

export interface Signals {
  /** The row of buttons. */
  element: HTMLElement;
  /** Show one that arrived. */
  receive: (signal: SignalKind, from: string | null) => void;
  destroy: () => void;
}

/** How long an incoming signal stays on screen. Long enough to read while walking. */
const TOAST_MS = 5000;

/** The wave takes over the screen. Everything else is a line at the bottom. */
const WAVE_FLASH_MS = 2200;

export function createSignals(options: SignalsOptions): Signals {
  const toast = el('output', { class: 'signal-toast', hidden: true, 'aria-live': 'polite' });
  const flash = el('div', { class: 'signal-flash', hidden: true, 'aria-hidden': 'true' });

  let toastTimer: number | null = null;
  let flashTimer: number | null = null;

  const buttons = el(
    'div',
    { class: 'signal-row' },
    ...SIGNALS.map((signal) =>
      el(
        'button',
        {
          class: `button button--small signal-row__button${signal.urgent ? ' signal-row__button--wave' : ''}`,
          type: 'button',
          'data-signal': signal.id,
          onclick: () => options.onSend(signal.id),
        },
        signal.label,
      ),
    ),
  );

  const element = el('div', { class: 'signals' }, buttons, toast, flash);

  return {
    element,

    receive: (signal, from) => {
      const who = from ?? 'They';
      toast.textContent =
        signal === 'wave'
          ? `${who} ${from ? 'is' : 'are'} waving — look up`
          : `${who}: ${signalLabel(signal)}`;
      toast.hidden = false;
      toast.classList.toggle('signal-toast--urgent', signal === 'wave');

      if (toastTimer !== null) window.clearTimeout(toastTimer);
      toastTimer = window.setTimeout(() => {
        toast.hidden = true;
      }, TOAST_MS);

      // A wave is meant to be seen from the corner of an eye while scanning a crowd, so it
      // takes the whole screen rather than politely queueing at the bottom.
      if (signal === 'wave') {
        flash.hidden = false;
        if (flashTimer !== null) window.clearTimeout(flashTimer);
        flashTimer = window.setTimeout(() => {
          flash.hidden = true;
        }, WAVE_FLASH_MS);
      }

      try {
        navigator.vibrate?.(signal === 'wave' ? [140, 70, 140, 70, 260] : [90]);
      } catch {
        // Haptics are a nicety, and a phone that refuses one is not a problem.
      }
    },

    destroy: () => {
      if (toastTimer !== null) window.clearTimeout(toastTimer);
      if (flashTimer !== null) window.clearTimeout(flashTimer);
      element.remove();
    },
  };
}
